import { useTopicLevelTwoTimeline } from '@op/pages/Summary/hooks/useTimelineData';
import React, { useMemo, useRef, useState } from 'react';
import { Bubble, Button, ConfigProvider, Table } from 'tea-component';
import TopicAnalysis from './TopicAnalysis';
import { useTableTopicLevelTwoList } from '@op/pages/Summary/hooks/useTopicConfig';
import { getLocale, useIntl } from 'umi';
import KeywordsModal from '@op/pages/Summary/components/KeywordsModal';
import { LanguageType } from '@op/types/common';
import CardCommon from '@op/components/CardCommon';
import SvgIcon from '@home/components/SvgIcon';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import TopicAnalysisModal from './TopicAnalysisModal';
import { getTeaLocale } from '@home/utils/lang';
import { NO_DATE } from '@op/constants';
import useTopicTrackingTableColumns from '@op/components/TableCommonHook/columns/topicTracking';
import './index.scss';
import { useUpdateEffect } from 'ahooks';

const { expandable, pageable, sortable, scrollable, autotip } = Table.addons;
export default function TopicLevelTwoTable({
  filter,
  transTopic,
  topics,
  language,
  goToFeeds,
  gameInfo,
  topN,
  topNRef,
  selectTopN,
  selectTopNRef,
  isTopicSentiment,
  sortData,
  topicList,
}: {
  filter: any;
  transTopic: any;
  topics?: Array<string>;
  language: LanguageType;
  goToFeeds: any;
  gameInfo?: any;
  topN?: number;
  topNRef?: React.RefObject<number>;
  selectTopN?: boolean;
  selectTopNRef?: React.RefObject<boolean>;
  isTopicSentiment?: string;
  sortData: (
    list: Array<any>,
    sort: { by: string; order: 'desc' | 'asc' },
  ) => Array<any>;
  topicList: any;
}) {
  const { formatMessage } = useIntl();
  const [visible, setVisible] = useState(false);
  const [showModalParams, setShowModalParams] = useState({
    tier1Topic: '',
    tier2Topic: '',
  });
  // // 当前展开的产品
  const [expandedKeys, setExpandedKeys] = useState([]);

  // 批量请求图表数据
  const {
    timelineMap,
    loading: chartLoading,
    apiError: chartApiError,
  } = useTopicLevelTwoTimeline({
    filter,
    topicLevel: 'L2',
    topic: topics,
  });

  const {
    records,
    pageInfo,
    setPageInfo,
    total,
    sort,
    setSort,
    loading,
    requestServerData,
  } = useTableTopicLevelTwoList({
    filter,
    topic: topics,
    topN: topNRef?.current as number,
    selectTopN: selectTopNRef?.current as boolean,
  });

  useUpdateEffect(() => {
    if (pageInfo.page_size !== 1000) {
      requestServerData();
    }
  }, [topN, selectTopN]);

  const tableRecords = useMemo(() => {
    if (!selectTopN) {
      return sortData(records || [], sort);
    }

    const list = (records || [])?.sort(
      sortable.comparer([{ by: 'mentions', order: 'desc' }]),
    );

    const topNList = list.slice(0, topN);

    return sortData(topNList || [], sort);
  }, [records, topN, selectTopN, pageInfo, sort]);

  const { columns, sortList } = useTopicTrackingTableColumns({
    timelineMap,
    filter,
    transTopic,
    topicLevel: 'L2',
    sortInfo: [sort, setSort],
    setPageInfo: setPageInfo,
    gameInfo,
    isTopicSentiment,
  });

  const [modalInfo, setModalInfo] = useState<{
    tableRecord: Array<any>;
    visible: boolean;
    currentTopic: string;
  }>({
    tableRecord: [],
    visible: false,
    currentTopic: '',
  });
  const stopRef = useRef(false);
  const allColumns = useMemo(() => {
    return [
      {
        key: 'keyTopics',
        header: formatMessage({ id: 'Tier 2 Topic' }),
        fixed: 'left',
        render: (record: any) => {
          return (
            <div
              className="dbo-topic-tracking-table-title dbo-topic-tracking-table-title--link"
              onClick={() => {
                if (topics) {
                  buriedBtnPoint('STO_tier2TopicOpenList', record.topic);
                  setModalInfo({
                    tableRecord: [record],
                    visible: true,
                    currentTopic: record.topic,
                  });
                }
              }}
            >
              <div className="dbo-topic-tracking-table-topic">
                {transTopic(record.topic)}
              </div>
              <Bubble
                dark={true}
                content={formatMessage({ id: 'Go to Feeds' })}
              >
                <div style={{ display: 'inline-block' }}>
                  <SvgIcon
                    type="view-details-icon"
                    onClick={() => {
                      goToFeeds(record.topic);
                      buriedBtnPoint('STO_TopicGoFeeds', record.topic);
                      stopRef.current = true;
                    }}
                  />
                </div>
              </Bubble>
            </div>
          );
        },
      },
      ...columns,
      {
        key: 'Action',
        header: formatMessage({ id: 'Action' }),
        width: 100,
        align: 'right',
        render: ({ topic }: any) => (
          <Button
            type="link"
            onClick={() => {
              setShowModalParams({
                tier1Topic: '',
                tier2Topic: topic,
              });
              buriedBtnPoint('STO_definition', topic);
              setVisible(true);
            }}
          >
            {formatMessage({ id: 'Definition' })}
          </Button>
        ),
      },
    ];
  }, [formatMessage, columns]);

  const locale = getTeaLocale(getLocale());

  function getTableAddons() {
    const addons = [
      sortable({
        columns: sortList.map((item) => ({
          prefer: 'desc',
          key: item,
        })),
        value: [sort],
        onChange: (value) => {
          const sort: any = value?.[0] || {
            by: 'mentions',
            order: 'desc',
          };
          buriedBtnPoint('STO_ListRank', sort.by);
          setSort(sort);
        },
      }),
      scrollable({
        minWidth: 1500,
      }),
      autotip({
        emptyText: formatMessage({ id: NO_DATE.SELECTION }),
      }),
    ];

    if (!topics) {
      addons.push(
        expandable({
          expandedKeys,
          rowExpand: true,
          onExpandedKeysChange: (keys, { operateKey, operateType }) => {
            if (stopRef.current) return;
            console.log('STO_TopicOpenList 2');
            if (operateType === 'expand') {
              buriedBtnPoint('STO_TopicOpenList', operateKey);
            }
            setExpandedKeys(keys);
          },
          render(record) {
            return (
              <TopicAnalysis
                language={language}
                transTopic={transTopic}
                filter={filter}
                sortData={sortData}
                topicList={topicList}
                topicLevel="L2"
                topics={[record.topic]}
                currentTopic={record.topic}
              />
            );
          },
        }),
      );
    }

    if (total > 10) {
      if (selectTopN) {
      } else if (pageInfo.page_size === 1000) {
        addons.push(pageable());
      } else {
        addons.push(
          pageable({
            recordCount: total,
            pageIndex: pageInfo.page,
            pageSize: pageInfo.page_size,
            onPagingChange: (query) => {
              const localPageInfo = {
                page: query.pageIndex || 1,
                page_size: query.pageSize || 10,
              };
              setPageInfo({ ...localPageInfo });
            },
          }),
        );
      }
    }
    return addons;
  }

  return (
    <>
      <KeywordsModal
        visible={visible}
        close={() => {
          setVisible(false);
          setShowModalParams({ tier1Topic: '', tier2Topic: '' });
        }}
        {...showModalParams}
      ></KeywordsModal>
      <CardCommon loading={loading} loadingHeight={565} contentCard>
        <ConfigProvider locale={locale}>
          <Table
            className="dbo-topic-tracking-table"
            bordered="all"
            records={tableRecords}
            recordKey={'topic'}
            columns={allColumns}
            addons={getTableAddons()}
          />
        </ConfigProvider>
      </CardCommon>

      {topics && (
        <TopicAnalysisModal
          visible={modalInfo.visible}
          transTopic={transTopic}
          filter={filter}
          sortData={sortData}
          language={language}
          topicList={topicList}
          records={modalInfo.tableRecord}
          columns={allColumns}
          currentTopic={modalInfo.currentTopic}
          close={() => {
            setModalInfo({ tableRecord: [], visible: false, currentTopic: '' });
          }}
        ></TopicAnalysisModal>
      )}
    </>
  );
}
